import { CommonButton } from "@components/CommonButton";
import { IconArrow, IconInputDel, IconLoading, IconFolder } from "@icons/index";
import { Input, Segmented } from "antd";
import { t } from "i18next";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { themeActionAtom } from "@src/jotai/theme";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SingleImageUpload from "./components/ImageUpload/SingleImageUpload";
import ProductLibraryModal from "@pages/aiStudio/components/ProductLibraryModal";
import { useAppDispatch } from "@hooks/redux";
import { setLibraryInit } from "@pages/videoCreating/videoCreatingSlice";
import report from "@report";
import { TestId } from "@shared/testIds";
import { fetchParseInfoByLink } from "@apis/http/api";
import type {
  ProductInfoByLinkResponse,
  ResultListUploadImageByUrlImageItem,
  SearchTiktokProductResult,
  SearchTiktokProductResultWithImages,
} from "@apis/videoCreatingService";
import { MAX_IMAGE_COUNT } from "@utils/const";
import { useSetState } from "ahooks";
import { ProductImageResult } from "@apis/upload";
import { ParseErrorTip } from "./ParseErrorTip";
import { ClipHttpStatus } from "@apis/http/types";
import { ClipErrorCode, resolveClipErrorCode, resolveClipErrorMessage } from "@utils/errrorMessage";
import { TextCarousel } from "./components/TextCarousel";
import { useLinkAutoParse } from "@hooks/useLinkAutoParse";
import { pushGtmEvent } from "@utils/gtm";
import { GtmEvent } from "@utils/gtmEvents";
import { IconLineImage, IconLineLink } from "@kalodata/kds-design-icons";

/** 去掉链接开头的 http:// 或 https://，用于输入框展示 */
const stripProtocol = (url: string) => url.replace(/^https?:\/\//i, "").trim();

/** 提交时确保链接带 https:// */
const ensureHttps = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

interface State {
  status: ClipHttpStatus;
  data: SearchTiktokProductResult | null;
  error: string | null;
  code: ClipErrorCode | undefined;
}

const PageVideoCreator = () => {
  const isDark = useAtomValue(themeActionAtom) === "dark";
  const [inputWorld, setInputWorld] = useState<string>("");
  const [showType, setShowType] = useState(0);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productInfo, setProductInfo] = useSetState<State>({
    status: "idle",
    data: null,
    error: null,
    code: undefined,
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // 链接解析（switchMap + AbortSignal 由 useLinkAutoParse 编排）
  const parseLink = async (url: string, signal: AbortSignal) => {
    if (!url.trim()) return;
    setProductInfo({ data: null, error: null, status: "loading", code: undefined });
    const keywordForApi = ensureHttps(url);
    try {
      const productResponse = (await fetchParseInfoByLink(
        { url: keywordForApi },
        { signal },
      )) as unknown as ProductInfoByLinkResponse;
      if (signal.aborted) return;
      let { images, product: parsedProductInfo } = productResponse;
      const nextImageList: ResultListUploadImageByUrlImageItem[] = (images || [])
        ?.filter((image) => image.success)
        .slice(0, MAX_IMAGE_COUNT)
        .map((image) => ({
          assetId: image.assetId,
          imageUrl: image.imageUrl,
          description: image.description ?? "",
          success: image.success ?? false,
          needsFaceMasking: image.needsFaceMasking ?? false,
        }));
      if (!parsedProductInfo) {
        parsedProductInfo = {
          id: "",
          country: "",
          productTitle: "",
          coverImageUrl: "",
          minRealPrice: "",
          maxRealPrice: "",
        } satisfies SearchTiktokProductResult;
      }
      setProductInfo({ status: "succeeded", data: parsedProductInfo, error: null, code: undefined });
      handleVideoCreatingInit({
        ...parsedProductInfo,
        images: nextImageList,
      });
    } catch (error: unknown) {
      if (signal.aborted) return;
      const resolved = resolveClipErrorMessage(error);
      const err = error as { message?: string };
      const displayError = resolved ? t(resolved.message, resolved.options) : (err?.message ?? null);
      setProductInfo({
        status: "failed",
        data: null,
        error: displayError,
        code: resolveClipErrorCode(error),
      });
    }
  };
  const { submit: submitLink, submitImmediately: submitLinkImmediately } = useLinkAutoParse(parseLink);
  const isProductSearchLoading = productInfo.status === "loading";

  const triggerProductSearch = () => {
    pushGtmEvent(GtmEvent.GoToCreateWithLink);
    report.click({ kalo_type: "commit_product_by_link" });
    submitLinkImmediately(inputWorld);
  };

  /**
   * 商品信息获取完成后进行视频创建参数初始化
   * @param product - 商品信息
   * @param type - 类型：image 图片表示图片上传，others 表示商品链接搜索、收藏夹获取
   * @returns
   */
  const handleVideoCreatingInit = async (
    product: SearchTiktokProductResultWithImages,
    type: "image" | "others" = "others",
  ) => {
    // 初始化视频创建参数
    await dispatch(
      setLibraryInit({
        product: {
          id: product.id,
          country: product.country || "US",
          productTitle: product.productTitle,
          priCateName: product.priCateName,
          secCateName: product.secCateName,
          terCateName: product.terCateName,
          // 商品链接搜索，也会包含images
          images: type === "image" ? product.images : (product.images ?? []),
        },
      }),
    );
    setShowProductModal(false);
    if (type === "image") {
      navigate(`/video-creating?productId=custom`);
    } else {
      navigate(`/video-creating?productId=${product.id}&country=${product.country || "US"}`);
    }
  };

  // 上传成功回调
  const handleUploadSuccess = (result: ProductImageResult[]) => {
    report.click({ kalo_type: "commit_product_by_upload_image" });
    handleVideoCreatingInit(
      {
        id: "",
        country: "",
        productTitle: "",
        coverImageUrl: "",
        minRealPrice: "",
        maxRealPrice: "",
        images: result.map<ResultListUploadImageByUrlImageItem>(
          (item) =>
            ({
              assetId: item.assetId,
              imageUrl: item.imageUrl,
              description: item.description ?? "",
              success: item.success,
              aiAnalyzed: false,
            }) satisfies ResultListUploadImageByUrlImageItem,
        ),
      },
      "image",
    );
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-full">
      <div className={classNames("text-[32px] font-bold", isDark ? "kalo-text-linear-gray" : "text-kds-fg")}>
        <Trans>AI Video Creator</Trans>
      </div>
      <div className="text-[14px] font-normal text-kds-fg-soft mt-[8px] mb-[40px]">
        <TextCarousel
          texts={[
            "One product link, instant promo videos",
            "Kalodata knows what sells. We turn those insights into your viral videos.",
          ]}
        />
      </div>
      <div className="mb-[20px]">
        <Segmented
          shape="round"
          value={showType}
          onChange={setShowType}
          options={[
            {
              value: 0,
              label: (
                <div className="flex items-center gap-[4px]">
                  <IconLineLink size={20} className="font-normal" />
                  <Trans>Product Link</Trans>
                </div>
              ),
            },
            {
              value: 1,
              label: (
                <div className="flex items-center gap-[4px]" data-testid={TestId.VideoCreateProductImageTab}>
                  <IconLineImage size={20} className="font-normal" />
                  <Trans>Product Image</Trans>
                </div>
              ),
            },
          ]}
        />
      </div>
      {showType === 1 && (
        <div className="w-[264px] h-[264px]">
          <SingleImageUpload
            onSuccess={handleUploadSuccess}
            onAddFromLibrary={() => setShowProductModal(true)}
            beforeUpload={() => {
              pushGtmEvent(GtmEvent.GoToUploadProductImage);
            }}
          />
        </div>
      )}
      {showType === 0 && (
        <div className="h-[264px]">
          <div className="relative w-[800px]">
            {isProductSearchLoading ? (
              <div
                className="w-[700px] h-[8px] left-0 right-0 mx-auto bottom-[6px] absolute rounded-full blur-[20px] animate-[marquee_3s_linear_infinite]"
                style={{
                  background: "linear-gradient(90deg, #0191F8 42.47%, #CF88FE 87.23%)",
                  backgroundSize: "200% 100%",
                }}
              />
            ) : (
              <div
                className="w-[700px] h-[12px] left-0 right-0 mx-auto bottom-[6px] absolute rounded-full blur-[20px]"
                style={{
                  background: "linear-gradient(90deg, #0191F8 42.47%, #CF88FE 87.23%)",
                  backgroundSize: "100% 100%",
                }}
              />
            )}
            <Input
              data-testid={TestId.ProductLinkInput}
              value={inputWorld}
              placeholder={t("Paste TikTok, Amazon (US, UK, JP, DE), or Kalodata product link")}
              size="large"
              prefix={
                <div className="font-semibold text-[14px] text-kds-fg flex items-center pl-[4px]">
                  <span>Https://</span>
                  <div className="w-px h-[40px] bg-kds-border-solid ml-[8px] mr-[4px]"></div>
                </div>
              }
              allowClear={{ clearIcon: <IconInputDel className="w-[16px] h-[16px]" /> }}
              suffix={
                <CommonButton
                  autoPath={false}
                  type="primary"
                  className="w-[40px] h-[40px] rounded-[8px]!"
                  icon={
                    <>
                      {isProductSearchLoading ? (
                        <IconLoading className="animate-spin icon" />
                      ) : (
                        <IconArrow invert className="w-[32px] h-[32px] rotate-90" />
                      )}
                    </>
                  }
                  onClick={triggerProductSearch}
                />
              }
              onKeyDown={(e) => {
                if ((e.code === "Enter" || e.code === "NumpadEnter") && e.keyCode !== 229) {
                  e.preventDefault();
                  triggerProductSearch();
                }
              }}
              onChange={(e) => {
                const next = stripProtocol(e.target.value);
                setInputWorld(next);
                submitLink(next);
              }}
            />
            <div className="absolute top-[80px] left-0 w-[800px] flex items-center justify-between">
              <ParseErrorTip
                status={productInfo.status}
                code={productInfo.code}
                error={productInfo.error}
                onTryUploadImage={() => setShowType(1)}
                onExtractVideoScript={() => {
                  navigate(`/insight?link=${encodeURIComponent(inputWorld)}`);
                }}
              />
              <div
                className="flex items-center gap-[4px] cursor-pointer whitespace-nowrap text-kds-fg-soft"
                onClick={() => setShowProductModal(true)}
              >
                <IconFolder className="w-[16px] h-[16px] text-black" />
                <Trans>Add from library</Trans>
              </div>
            </div>
          </div>
        </div>
      )}
      {showProductModal && (
        <ProductLibraryModal
          open={showProductModal}
          onClose={() => setShowProductModal(false)}
          onSubmit={async (product: any) => {
            report.click({ kalo_type: "commit_product_from_library" });
            handleVideoCreatingInit(product);
          }}
        />
      )}
    </div>
  );
};

export default PageVideoCreator;
